import BrutalCard from "@/components/common/BrutalCard";
import SectionHeading from "@/components/common/SectionHeading";
import CTASection from "@/components/sections/CTASection";

const VALUES = [
    {
        n: "01",
        title: "Bold over polite",
        copy: "We make opinionated calls. The work shouldn't try to please everyone — it should move the right people.",
    },
    {
        n: "02",
        title: "Throughput over theatre",
        copy: "Less Slack noise. Fewer status meetings. More demos, more decisions, more shipped.",
    },
    {
        n: "03",
        title: "Loops over launches",
        copy: "Every system we build is designed to compound. Loops beat campaigns, every quarter.",
    },
];

const TEAM = [
    { name: "Gangesh Tripathi", role: "Co-founder · Strategy", bg: "yellow" },
    { name: "Ravikant Pal", role: "Engineering Lead", bg: "pink" },
    { name: "Vikas Kumar", role: "Design Director", bg: "green" },
    { name: "Swastik Mishra", role: "Growth Lead", bg: "blue" },
];

export default function About() {
    return (
        <div data-testid="about-page">
            <section className="border-b-4 border-ink py-20 md:py-28">
                <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 grid lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-7">
                        <span className="label-mono inline-block bg-brand-yellow border-2 border-ink rounded-full px-3 py-1">
                            Our story
                        </span>
                        <h1 className="mt-5 font-display text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.92] tracking-tighter">
                            We forge software{" "}
                            <span className="bg-brand-orange text-white inline-block px-3 -rotate-1 border-2 border-ink shadow-brutal">
                                that ships
                            </span>{" "}
                            and marketing that compounds.
                        </h1>
                    </div>
                    <div className="lg:col-span-5">
                        <p className="font-body text-ink/75 text-lg leading-relaxed">
                            Technovale Solutions started in 2019 as a two-person
                            studio shipping internal tools out of Gurugram. Six
                            years later, we are 18 operators across engineering,
                            design, and growth — still shipping, still opinionated,
                            still allergic to status decks.
                        </p>
                    </div>
                </div>
            </section>

            <section className="border-b-4 border-ink py-24 md:py-32 bg-paper">
                <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5">
                        <BrutalCard bg="pink">
                            <div className="aspect-[4/5] rounded-xl overflow-hidden border-2 border-ink">
                                <img
                                    src="https://images.pexels.com/photos/6985268/pexels-photo-6985268.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                                    alt="Studio"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </BrutalCard>
                    </div>
                    <div className="lg:col-span-7">
                        <SectionHeading eyebrow="What we believe" title="Three rules we don't break." />
                        <div className="mt-10 space-y-6">
                            {VALUES.map((v) => (
                                <BrutalCard
                                    key={v.n}
                                    bg="white"
                                    interactive
                                    data-testid={`about-value-${v.n}`}
                                    className="grid grid-cols-12 gap-6 items-start"
                                >
                                    <div className="col-span-3 md:col-span-2">
                                        <span className="font-mono text-5xl md:text-6xl font-bold text-brand-blue leading-none">
                                            {v.n}
                                        </span>
                                    </div>
                                    <div className="col-span-9 md:col-span-10">
                                        <h3 className="font-display text-2xl md:text-3xl font-black leading-tight">
                                            {v.title}
                                        </h3>
                                        <p className="mt-2 font-body text-ink/75 leading-relaxed">
                                            {v.copy}
                                        </p>
                                    </div>
                                </BrutalCard>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="border-b-4 border-ink py-24 md:py-32">
                <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
                    <SectionHeading eyebrow="The team" title="A small pod of operators." />
                    <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6">
                        {TEAM.map((m, i) => (
                            <BrutalCard
                                key={m.name}
                                bg={m.bg}
                                interactive
                                data-testid={`about-team-${i}`}
                                className="aspect-square flex flex-col justify-end"
                            >
                                <p className="label-mono">0{i + 1}</p>
                                <h4 className="mt-2 font-display text-xl md:text-2xl font-black leading-tight">
                                    {m.name}
                                </h4>
                                <p className="font-body text-sm">{m.role}</p>
                            </BrutalCard>
                        ))}
                    </div>
                </div>
            </section>

            <CTASection />
        </div>
    );
}
